import { exportCompanyChartReport } from "@commenda/api";
import { formatLongDate, sanitizeFilename } from "@commenda/utils";
import { type ReactFlowInstance, type Rect } from "@xyflow/react";
import { toPng } from "html-to-image";
import { ENTITY_NODE_HEIGHT, ENTITY_NODE_WIDTH } from "../OrgDiagram/graph/layout";

// Same hex as the canvas wrapper's `bg-bg-light` token.
const BG = "#fcfbf8";

// Canvas matches the graph's bounds + padding; `pixelRatio` drives crispness.
const PADDING = 80;
const TARGET_PIXEL_RATIO = 4;
// Chrome's per-axis canvas cap is ~16384px; staying under prevents dense
// graphs from failing mid-export.
const MAX_PIXELS = 16000;
// Safari also caps a canvas's total area, so very large charts drop resolution
// instead of failing.
const MAX_TOTAL_PIXELS = 64_000_000;

// html-to-image copies every listed property onto every cloned element. With CSS
// variables included, a large chart outgrew Safari's 64MB data-URL limit, and the
// computed values already have them resolved. Safari 27 squares every corner when
// all the corner-shape longhands are set, and round is their default anyway.
// html-to-image keeps the first list it gets, so every capture passes this one.
export const getCaptureStyleProperties = (): string[] =>
  Array.from(getComputedStyle(document.documentElement)).filter(
    (name) => !name.startsWith("--") && !name.startsWith("corner-"),
  );

const buildFilename = (
  companyName: string | undefined,
  ext: "png" | "pdf",
): string => {
  const base = sanitizeFilename(companyName, "Organization");
  return `${base} - Organization Structure - ${formatLongDate()}.${ext}`;
};

const triggerDownload = (dataUrl: string, filename: string): void => {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// Capture pattern: html-to-image's `toPng` clones into an offscreen SVG
// foreignObject and applies the transform on the clone — live viewport never
// jitters. Targets `.react-flow__viewport` (not `.react-flow`) so the
// unlinked panel and floating toolbar are excluded.
//
// Hand-rolled bounds (vs xyflow getNodesBounds) because the latter reads
// measured DOM dims which can be stale before the resize observer settles,
// under-reporting width and clipping the rightmost node.
function computeNodesBounds(rf: ReactFlowInstance): Rect {
  const nodes = rf.getNodes();
  if (nodes.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    const w = n.width ?? ENTITY_NODE_WIDTH;
    const h = n.height ?? ENTITY_NODE_HEIGHT;
    minX = Math.min(minX, n.position.x);
    minY = Math.min(minY, n.position.y);
    maxX = Math.max(maxX, n.position.x + w);
    maxY = Math.max(maxY, n.position.y + h);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export interface Capture {
  dataUrl: string;
  domWidth: number;
  domHeight: number;
}

function getCapturePixelRatio(domWidth: number, domHeight: number): number {
  return Math.min(
    TARGET_PIXEL_RATIO,
    MAX_PIXELS / domWidth,
    MAX_PIXELS / domHeight,
    Math.sqrt(MAX_TOTAL_PIXELS / (domWidth * domHeight)),
  );
}

// `container` scopes the viewport lookup so a hidden offscreen capture
// doesn't accidentally grab the live on-screen diagram (and vice versa).
// `contentBounds` covers charts that draw beyond their nodes (labels, chips,
// trays); without it the capture frames the nodes alone.
export async function captureChart(
  rf: ReactFlowInstance,
  backgroundColor: string | undefined,
  container?: HTMLElement,
  contentBounds?: Rect,
): Promise<Capture> {
  const root: Document | HTMLElement = container ?? document;
  const viewportEl = root.querySelector<HTMLElement>(".react-flow__viewport");
  if (!viewportEl) throw new Error("ReactFlow viewport not found");

  const bounds = contentBounds ?? computeNodesBounds(rf);
  const domWidth = Math.max(1, bounds.width) + 2 * PADDING;
  const domHeight = Math.max(1, bounds.height) + 2 * PADDING;

  const pixelRatio = getCapturePixelRatio(domWidth, domHeight);

  const dataUrl = await toPng(viewportEl, {
    backgroundColor,
    width: domWidth,
    height: domHeight,
    pixelRatio,
    includeStyleProperties: getCaptureStyleProperties(),
    filter: (node) =>
      !(node instanceof HTMLElement && node.dataset.captureSkip !== undefined),
    style: {
      width: `${domWidth}px`,
      height: `${domHeight}px`,
      // No scale — DOM is 1:1 with world coords; pixelRatio handles density.
      transform: `translate(${PADDING - bounds.x}px, ${PADDING - bounds.y}px)`,
    },
  });

  return { dataUrl, domWidth, domHeight };
}

export async function exportOrgChartPng(
  rf: ReactFlowInstance,
  companyName?: string,
  contentBounds?: Rect,
): Promise<void> {
  const { dataUrl } = await captureChart(rf, undefined, undefined, contentBounds);
  triggerDownload(dataUrl, buildFilename(companyName, "png"));
}

// Typed so callers can branch on unsupported-browser vs other failures.
export class ClipboardUnsupportedError extends Error {
  constructor() {
    super('Clipboard image write is not supported in this browser.');
    this.name = 'ClipboardUnsupportedError';
  }
}

export async function copyOrgChartPngToClipboard(
  rf: ReactFlowInstance,
  contentBounds?: Rect,
): Promise<void> {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
    throw new ClipboardUnsupportedError();
  }
  // BG (not transparent): the system clipboard doesn't reliably preserve PNG
  // alpha, so an alpha capture pastes as black blocks in Slack/Notion/etc.
  // Safari only allows the write while the click is being handled, so the PNG goes
  // in as a promise instead of being awaited first.
  const png = captureChart(rf, BG, undefined, contentBounds)
    .then(({ dataUrl }) => fetch(dataUrl))
    .then((response) => response.blob());
  await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
}

export async function exportOrgChartPdf(
  rf: ReactFlowInstance,
  companyId: number,
  companyName?: string,
  contentBounds?: Rect,
): Promise<void> {
  const { dataUrl } = await captureChart(rf, undefined, undefined, contentBounds);
  await exportCompanyChartReport(companyId, "organization-chart", companyName, dataUrl);
}
